/* eslint-disable react/jsx-no-duplicate-props*/
import { InputAdornment, withStyles } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import supplementationService from "../../../supplementationService";
import formulaService from "features/formulacao/formulaService";
import { DateTimePicker } from "material-ui-pickers";
import CalendarIcon from "@material-ui/icons/Today";
import { DATE_FORMATS, DATE_UNIT, DateUtils } from "shared/dateUtils";
import { validate } from "../../shared/validators";
import _ from "lodash";
import SkeletonLoading from "features/shared/components/SkeletonLoading";
import NumberField from "features/shared/components/NumberField";
import SelectDropdown from "components/SelectDropdown";
import CustomTextField from "features/shared/components/CustomTextField";
import CustomLabel from "features/shared/components/CustomLabel";
import { parseDateToFarmTimezone } from "features/collect/supplementation/supplementationUtils";
import { useConfirmDialog } from "features/shared/hooks/useConfirmDialog";
import { useTranslations } from "adapters/i18n";

function SupplementationFormCard({
  forceDisableAll,
  supplementationId,
  supplementationFetched,
  onChangeValues = () => {},
  onFormValidation = () => {},
  classes,
}) {
  const { t } = useTranslations();
  const [supplementation, setSupplementation] = useState({});
  const [formulas, setFormulas] = useState([]);
  const [values, _setValues] = useState({});
  const [errors, setErrors] = useState({});

  const [unit, setUnit] = useState("");

  const {
    render: renderFormulaCreepAlert,
    setVisible: setVisibleFormulaCreepAlert,
    setText: setTextFormulaCreepAlert,
  } = useConfirmDialog({
    title: t("common:collect-edition"),
    onCancel: () => {
      _setValues({ values, formulaId: supplementation.formulaId });
    },
  });

  const runValidation = (valuesToValidate) => {
    if (forceDisableAll) return;
    const errorsForm = validate(valuesToValidate, t);
    if (Object.keys(errorsForm).length) {
      onFormValidation(true);
    } else {
      onFormValidation(false);
    }
    setErrors(errorsForm);
  };

  const checkValueFormula = () => {
    if (
      values.formulaId &&
      Number(values.formulaId) !== Number(supplementation.formulaId)
    ) {
      const formulaSelecionada = formulas.find(
        (el) => el.IdFormula === values.formulaId
      );
      if (!formulaSelecionada) return;
      if (formulaSelecionada.FormulaCreep && !supplementation.troughCreep) {
        setTextFormulaCreepAlert(
          t("supplementation:standard-trough-creep-formula-text", {
            formulaName: formulaSelecionada.Nome,
          })
        );
        setVisibleFormulaCreepAlert(true);
      } else if (
        !formulaSelecionada.FormulaCreep &&
        supplementation.troughCreep
      ) {
        setTextFormulaCreepAlert(
          t("supplementation:creep-trough-standard-formula-text", {
            formulaName: formulaSelecionada.Nome,
          })
        );
        setVisibleFormulaCreepAlert(true);
      }
    }
  };

  useEffect(checkValueFormula, [
    values.formulaId,
    supplementation.formulaId,
    supplementation.troughCreep,
  ]);

  const getBasicFields = () => ({
    formulaId: supplementation.formulaId,
    amountTroughBags: supplementation.amountTroughBags,
    collectionDate: supplementation.collectionDate,
  });

  const setValue = (key) => (event) => {
    const basicFields = getBasicFields();
    const value = event.target.value;
    const newValues = { ...basicFields, ...values, [key]: value };
    if (key === "formulaId") {
      const formula = formulas.find((f) => f.IdFormula === value);
      if (formula) {
        if (formula.Granel) {
          setUnit(t("common:measure.kg"));
        } else {
          setUnit(t("common:measure.bags"));
        }
      } else {
        newValues.amountTroughBags = "";
      }
    }
    runValidation(newValues);
    onChangeValues(newValues);
    _setValues(newValues);
  };

  const setValueDate = (key) => (date) => {
    const basicFields = getBasicFields();
    const newValues = {
      ...basicFields,
      ...values,
      [key]: DateUtils.parse(date).toISOString(),
    };
    runValidation(newValues);
    onChangeValues(newValues);
    _setValues({ ...newValues, [key]: date });
  };

  const sortAndConvert = (items, group) => {
    return items
      .sort((a, b) => (a.Nome.toUpperCase() < b.Nome.toUpperCase() ? -1 : 1))
      .map((formula) => ({
        value: formula.IdFormula,
        label: formula.Nome,
        group,
      }));
  };

  const prepareFormulasOption = (formulas) => {
    if (formulas.length) {
      const result = _.partition(formulas, (el) => el.Excluido);
      const inactive = sortAndConvert(
        result[0],
        t("common:status.inactive-plural-fem")
      );
      const active = sortAndConvert(
        result[1],
        t("common:status.active-plural-fem")
      );
      return [...(active || []), ...(inactive || [])];
    } else {
      return [
        { value: null, label: t("common:not-informed-fem").toUpperCase() },
      ];
    }
  };

  const prepare = async () => {
    let _supplementation = supplementationFetched;

    if (!_supplementation) {
      const result = await supplementationService.getSupplementation(
        supplementationId
      );
      _supplementation = result.data;
    }

    _setValues({
      formulaId: _supplementation.formulaId,
      amountTroughBags: _supplementation.amountTroughBags,
      collectionDate: _supplementation.collectionDate,
    });

    const farmId = _supplementation.farmId;

    if (!forceDisableAll) {
      const formulas = await formulaService.listarFormulasPorFazenda(
        farmId,
        true
      );
      setFormulas(formulas.data.body);
    } else {
      setFormulas([
        {
          IdFormula: _supplementation.formulaId,
          Nome:
            _supplementation.formulaName ||
            t("common:not-informed-fem").toUpperCase(),
        },
      ]);
    }
    setSupplementation(_supplementation);

    _supplementation.amountTroughBags = (
      _supplementation.amountTroughBags || ""
    )
      .toString()
      .replace(".", ",");

    runValidation(_supplementation);

    onChangeValues({
      formulaId: _supplementation.formulaId,
      amountTroughBags: _supplementation.amountTroughBags,
      collectionDate: _supplementation.collectionDate,
    });

    if (_supplementation.formulaFractionable) {
      setUnit(t("common:measure.kg"));
    } else {
      setUnit(t("common:measure.bags"));
    }
  };

  useEffect(() => {
    prepare();
  }, [supplementationId, supplementationFetched]);

  const formulasSelect = prepareFormulasOption(formulas);

  const checkTroughType = () => {
    if (supplementation.creepCollection) {
      return t("supplementation:creep-trough");
    }
    if (supplementation.sharedTrough) {
      return t("supplementation:shared-trough");
    }
    return t("supplementation:standard-trough");
  };

  const checkQuality = () => {
    if (Number.isInteger(supplementation.quality)) {
      if (supplementation.quality > 0) {
        return t("supplementation:quality.good");
      }
      if (supplementation.quality === 0) {
        return t("supplementation:quality.bad");
      }
    }
    return t("common:not-informed-fem");
  };

  const checkTroughScore = () => {
    if (supplementation.volume <= 1) {
      return t("supplementation:trough-volume-score.empty");
    }
    if (supplementation.volume >= 3) {
      return t("supplementation:trough-volume-score.full");
    }
    return t("supplementation:trough-volume-score.adequate");
  };

  const checkTroughAccess = () => {
    if (supplementation.goodAccess > 0) {
      return t("supplementation:trough-access-score.good");
    }
    return t("supplementation:trough-access-score.bad");
  };

  const checkTroughStructure = () => {
    if (supplementation.goodStructure > 0) {
      return t("supplementation:trough-structure-score.good");
    }
    return t("supplementation:trough-structure-score.bad");
  };

  const checkDate = () => {
    if (values.collectionDate) {
      return parseDateToFarmTimezone(values.collectionDate);
    }
    if (supplementation.collectionDate) {
      return parseDateToFarmTimezone(supplementation.collectionDate);
    }
    return DateUtils.toDate();
  };

  const renderSkeleton = () => {
    return (
      <SkeletonLoading width="100%" height="365px" borderRadius="16px 16px" />
    );
  };

  if (!supplementation.id || !formulas.length) {
    return renderSkeleton();
  }

  const onChangeFormula = (value) =>
    setValue("formulaId")({ target: { value } });

  return (
    <div className={classes.container}>
      <h2 className={classes.title}>
        {supplementation.formulaName ||
          t("supplementation:not-informed-formulation").toUpperCase()}
      </h2>
      <div className={classes.content}>
        <div className={classes.fieldLine}>
          <CustomTextField
            classes={classes}
            testId={`inputTroughType`}
            id="troughType"
            label={t("supplementation:trough-type")}
            value={checkTroughType()}
            flex={3}
          />
          <CustomTextField
            classes={classes}
            testId={`inputTroughScore`}
            id="troughScore"
            label={t("supplementation:trough-score")}
            value={checkTroughScore()}
          />
          <CustomTextField
            classes={classes}
            testId={`inputTroughQuality`}
            id="troughQuality"
            label={t("supplementation:trough-quality")}
            value={checkQuality()}
          />
          <CustomTextField
            classes={classes}
            testId={`inputTroughReplenished`}
            id="troughReplenished"
            label={t("supplementation:trough-was-repleinished")}
            value={
              supplementation.replenished
                ? t("common:actions.yes")
                : t("common:actions.no")
            }
          />
        </div>
        <div className={classes.fieldLine}>
          <div className={classes.fieldFlex} style={{ flex: 3 }}>
            <SelectDropdown
              id="formulaId"
              data-testid="inputTroughAccess"
              isDisabled={forceDisableAll || !supplementation.replenished}
              label={{
                text: t("supplementation:formulation"),
                required: true,
              }}
              error={!!errors.formula}
              helperText={errors.Coberto}
              options={formulasSelect}
              groupBy={(option) => option.group}
              emptyText={t("common:not-informed-fem").toUpperCase()}
              value={
                forceDisableAll ? supplementation.formulaId : values.formulaId
              }
              onChange={onChangeFormula}
            />
          </div>
          <div className={classes.fieldFlex} style={{ flex: 2 }}>
            <NumberField
              data-testid={"inputQuantity"}
              id="quantity"
              InputLabelProps={{ shrink: true }}
              className={classes.textField}
              label={
                <CustomLabel
                  classes={classes}
                  label={t("common:quantity-supplied")}
                  tooltip={
                    forceDisableAll
                      ? ""
                      : t("common:can-not-change-unit-message")
                  }
                />
              }
              value={
                forceDisableAll
                  ? supplementation.amountTroughBags
                  : values.amountTroughBags
              }
              disabledNullText={t("supplementation:not-fed").toUpperCase()}
              onChange={setValue("amountTroughBags")}
              inputProps={{
                style: {
                  paddingBottom: "13px",
                },
              }}
              InputProps={{
                endAdornment: values.formulaId ? (
                  <InputAdornment position="end">{unit}</InputAdornment>
                ) : null,
              }}
              error={!!errors.amountTroughBags}
              helperText={errors.amountTroughBags}
              disabled={
                forceDisableAll ||
                !values.formulaId ||
                !supplementation.replenished ||
                false
              }
            />
          </div>
          <CustomTextField
            classes={classes}
            testId={`inputQuality`}
            id="quality"
            label={t("supplementation:trough-access")}
            value={checkTroughAccess()}
          />
          <CustomTextField
            classes={classes}
            testId={`inputTroughStructure`}
            id="troughStructure"
            label={t("supplementation:trough-structure")}
            value={checkTroughStructure()}
          />
        </div>

        <div className={classes.fieldLine}>
          <CustomTextField
            classes={classes}
            flex={3}
            testId={`inputPastureName`}
            id="pastureName"
            label={t("locales:pasture")}
            value={
              supplementation.pastureName
                ? supplementation.pastureName.join(", ")
                : ""
            }
          />
          <CustomTextField
            classes={classes}
            flex={3}
            testId={`inputCreationUser`}
            id="creationUser"
            label={t("common:responsible-for-collect")}
            value={`${supplementation.creationUser}`}
          />
          <div className={classes.fieldFlex} style={{ flex: 2 }}>
            <DateTimePicker
              okLabel={t("common:actions.select")}
              cancelLabel={t("common:actions.cancel")}
              data-testid={`inputCollectionDate`}
              InputLabelProps={{ shrink: true }}
              ampm={false}
              disableFuture
              disabled={forceDisableAll}
              className={classes.datePickerField}
              label={
                <CustomLabel
                  classes={classes}
                  label={t("common:operation-date")}
                  tooltip={
                    forceDisableAll
                      ? ""
                      : t(
                          "common:validation.sixty-days-before-today-to-edit-date"
                        )
                  }
                />
              }
              value={checkDate()}
              onChange={setValueDate("collectionDate")}
              maxDate={DateUtils.now()}
              minDate={
                forceDisableAll
                  ? DateUtils.now()
                  : DateUtils.now().subtract(60, DATE_UNIT.DAY).toDate()
              }
              format={DATE_FORMATS["DD/MM/YYYY HH:mm"]}
              minDateMessage={t(
                "common:validation.sixty-days-before-today-to-edit-date"
              )}
              maxDateMessage={t("common:validation.not-select-future-date")}
              invalidDateMessage={t("common:validation.invalid-date")}
              helperText={forceDisableAll ? "" : errors.collectionDate}
              error={!forceDisableAll && errors.collectionDate}
              inputProps={{
                style: {
                  paddingBottom: "13px",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CalendarIcon />
                  </InputAdornment>
                ),
                style: {
                  paddingBottom: "5px",
                },
              }}
            />
          </div>

          <div className={classes.fieldFlex} style={{ flex: 1 }}></div>
        </div>
      </div>
      {renderFormulaCreepAlert()}
    </div>
  );
}

const styles = {
  container: {
    padding: "32px",
    borderRadius: "16px",
    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.2)",
    border: "1px solid #e5e5ea",
    backgroundColor: "#fff",
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  textField: {
    backgroundColor: "#fff",
    width: "100%",
  },
  datePickerField: {
    width: "100%",
  },
  title: {
    marginBottom: "32px",
    fontFamily: "Roboto",
    fontSize: "14px",
    fontWeight: "bold",
    letterSpacing: "0.5px",
    color: "#444",
  },
  fieldLine: {
    display: "flex",
    flexDirection: "row",
    gap: "32px",
  },
  fieldFlex: {},
};

export default withStyles(styles)(SupplementationFormCard);
